"use client";

import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/navigation";
import { fetchWithAuth } from "@/lib/auth";
import { Bell, CheckCheck, ChevronRight } from "lucide-react";
import { admissionsQueueUrl, boardQueueUrl, getAccess } from "@/lib/admissions-committee/api";

export type NotificationType = {
    id: string;
    title: string;
    message: string;
    is_read: boolean;
    created_at: string;
    entity_type?: string;
    entity_id?: string;
    tag?: string;
    category?: string;
};

export function isEventNotification(notif: NotificationType): boolean {
    if (notif.category === "event") return true;
    const et = (notif.entity_type || "").toLowerCase();
    if (et === "meeting" || et === "event") return true;
    const tag = (notif.tag || "").toLowerCase();
    const eventTags = [
        "pre_event",
        "mid_event",
        "post_event",
        "post_event_review",
        "post_published",
        "phase4_published",
        "phase4_submitted",
        "nag",
        "debrief",
        "debrief_auto",
        "chair_review",
    ];
    if (eventTags.includes(tag)) return true;
    const title = (notif.title || "").toLowerCase();
    if (
        title.startsWith("pre-event") ||
        title.startsWith("mid-event") ||
        title.startsWith("post-event") ||
        title.includes("reminder: in-person") ||
        title.includes("reminder: virtual") ||
        title.startsWith("event approved") ||
        title.startsWith("chair review") ||
        title.startsWith("published:") ||
        title.includes("debrief")
    ) {
        return true;
    }
    return false;
}

function getPersonalTagBadge(entityType?: string, tag?: string): { label: string; bg: string; text: string } {
    const et = (entityType || "").toLowerCase();
    const t = (tag || "").toLowerCase();

    if (et === "admissions_review" || t === "admissions") {
        return { label: "Admissions", bg: "bg-indigo-50 border-indigo-200", text: "text-indigo-700" };
    }
    if (et === "committee" || t === "committee") {
        return { label: "Committee", bg: "bg-purple-50 border-purple-200", text: "text-purple-700" };
    }
    if (et === "recognition" || t === "badge") {
        return { label: "Recognition", bg: "bg-amber-50 border-amber-200", text: "text-amber-700" };
    }
    if (et === "gig" || t === "opportunity") {
        return { label: "Opportunity", bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700" };
    }
    if (et === "platform" || t.includes("error") || t.includes("spike")) {
        return { label: "Platform", bg: "bg-rose-50 border-rose-200", text: "text-rose-700" };
    }
    if (et === "system" || t === "welcome") {
        return { label: "System", bg: "bg-teal-50 border-teal-200", text: "text-teal-700" };
    }
    return { label: "Personal", bg: "bg-slate-50 border-slate-200", text: "text-slate-700" };
}

function formatRelativeTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function NotificationBell({
    onViewAll,
}: {
    onViewAll?: () => void;
} = {}) {
    const router = useRouter();
    const [notifications, setNotifications] = useState<NotificationType[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isMarkingAll, setIsMarkingAll] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const fetchNotifications = async () => {
        try {
            const notifRes = await fetchWithAuth(`/api/v1/notifications?category=personal&limit=50`);
            if (notifRes.ok) {
                const notifs: NotificationType[] = await notifRes.json();
                // Strictly exclude any event/meeting lifecycle notice
                const personalOnly = (notifs || []).filter(n => !isEventNotification(n));
                personalOnly.sort(
                    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
                setNotifications(personalOnly);
            }
        } catch (err) {
            console.error("Failed to load personal notifications:", err);
        }
    };

    const markRead = async (id: string) => {
        try {
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
            await fetchWithAuth(`/api/v1/notifications/${id}/read`, { method: "POST" });
        } catch (err) {
            console.error("Failed to mark personal notification as read", err);
        }
    };

    const markAllRead = async () => {
        if (isMarkingAll) return;
        setIsMarkingAll(true);
        try {
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            await fetchWithAuth(`/api/v1/notifications/read-all?category=personal`, { method: "POST" });
        } catch (err) {
            console.error("Failed to mark all personal notifications as read", err);
        } finally {
            setIsMarkingAll(false);
        }
    };

    const handleNotificationClick = async (notif: NotificationType) => {
        if (!notif.is_read) {
            markRead(notif.id);
        }

        if (notif.entity_type === "admissions_review" && notif.entity_id) {
            setIsOpen(false);
            try {
                const access = await getAccess();
                // A board member who is not on the committee has no access to
                // the committee's queue — sending them there would land them on
                // a 403. Their door to the same application is the Board's own
                // tab. Someone on both keeps the committee route, which is the
                // earlier half of the application's life.
                const url =
                    access.is_board_member && !access.is_committee_member
                        ? boardQueueUrl(access.board_committee_id, notif.entity_id)
                        : admissionsQueueUrl(access.admissions_committee_id, notif.entity_id);
                router.push(url);
            } catch (e) {
                // The capability call failed — an expired session, or the
                // committee could not be resolved. Land them somewhere rather
                // than leaving the click as a silent no-op, which is what a
                // bare console.error made it.
                console.error("Failed to route admissions notification:", e);
                router.push("/dashboard");
            }
            return;
        }

        if (notif.entity_type === "gig") {
            setIsOpen(false);
            router.push("/dashboard?section=work");
            return;
        }

        if (notif.entity_type === "recognition") {
            setIsOpen(false);
            router.push("/dashboard?section=recognition");
            return;
        }

        setIsOpen(false);
    };

    useEffect(() => {
        fetchNotifications();
        const intervalId = setInterval(fetchNotifications, 60000);
        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const unreadCount = notifications.filter(n => !n.is_read).length;

    return (
        <div className="relative" ref={dropdownRef}>
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className={`relative p-2 rounded-xl transition-all ${
                    isOpen 
                        ? "bg-[#003d45]/10 text-[#003d45]" 
                        : "hover:bg-black/5 dark:hover:bg-white/10 text-slate-700"
                }`}
                aria-label="Personal Notifications"
                title="Notifications"
            >
                <Bell className="w-5 h-5" strokeWidth={2.2} />
                {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] rounded-full bg-[#ae232d] text-white text-[9px] font-black flex items-center justify-center px-0.5 shadow-sm ring-2 ring-white">
                        {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-84 sm:w-96 max-h-[30rem] overflow-hidden flex flex-col bg-white border border-slate-200 shadow-2xl rounded-2xl z-50 text-slate-800 font-sans animate-in fade-in zoom-in-95 duration-150">
                    {/* Header */}
                    <div className="p-3.5 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Bell className="w-4 h-4 text-[#003d45]" />
                            <span className="font-bold text-sm text-[#003d45]">Notifications</span>
                            {unreadCount > 0 && (
                                <span className="text-[10px] font-black bg-[#ae232d] text-white px-1.5 py-0.5 rounded-full">
                                    {unreadCount} New
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllRead}
                                    disabled={isMarkingAll}
                                    className="text-[11px] font-semibold text-[#007a8a] hover:text-[#003d45] flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-slate-200/60 transition-colors disabled:opacity-50"
                                    title="Mark all notifications read"
                                >
                                    <CheckCheck className="w-3.5 h-3.5" />
                                    <span>Mark all read</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Notification List */}
                    <div className="flex-1 overflow-y-auto divide-y divide-slate-100 max-h-80">
                        {notifications.length === 0 ? (
                            <div className="p-8 text-center">
                                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3 text-slate-400">
                                    <Bell className="w-6 h-6" />
                                </div>
                                <p className="text-xs font-semibold text-slate-700">All caught up!</p>
                                <p className="text-[11px] text-slate-400 mt-1 max-w-[14rem] mx-auto">
                                    You have no pending committee reviews or personal notices.
                                </p>
                            </div>
                        ) : (
                            notifications.map((notif) => {
                                const badge = getPersonalTagBadge(notif.entity_type, notif.tag);
                                return (
                                    <div 
                                        key={notif.id} 
                                        className={`p-3.5 text-sm transition-all cursor-pointer group ${
                                            notif.is_read 
                                                ? "bg-white hover:bg-slate-50/80 opacity-80 hover:opacity-100" 
                                                : "bg-[#f8f9fa] hover:bg-[#f1f3f5]"
                                        }`}
                                        onClick={() => handleNotificationClick(notif)}
                                    >
                                        <div className="flex items-center justify-between gap-2 mb-1.5">
                                            <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md border ${badge.bg} ${badge.text}`}>
                                                {badge.label}
                                            </span>
                                            <span className="text-[10px] font-medium text-slate-400">
                                                {formatRelativeTime(notif.created_at)}
                                            </span>
                                        </div>
                                        <div className="flex items-start justify-between gap-2">
                                            <b className={`text-xs block leading-snug ${notif.is_read ? 'text-slate-700 font-semibold' : 'text-slate-900 font-bold'}`}>
                                                {notif.title}
                                            </b>
                                            <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#007a8a] group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-0.5" />
                                        </div>
                                        <p className={`text-[11px] mt-1 line-clamp-2 leading-relaxed ${notif.is_read ? 'text-slate-500' : 'text-slate-600'}`}>
                                            {notif.message}
                                        </p>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer */}
                    {onViewAll && notifications.length > 0 && (
                        <div className="border-t border-slate-100 bg-slate-50/50 px-3.5 py-2 text-center text-xs">
                            <button
                                onClick={() => { setIsOpen(false); onViewAll(); }}
                                className="w-full text-center text-[11px] font-bold text-slate-500 hover:text-slate-800 transition-colors"
                            >
                                View notices
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
